import React from 'react';
import formatDistanceStrict from 'date-fns/formatDistanceStrict';
import Typo from '../../Typography/Typo';
import ThreeDotsIcon from '../../Icons/ThreeDotsIcon';
import CommentDropdownMenu from './CommentDropdownMenu';
import Dropdown from '../../Dropdown/Dropdown';
import { CommentInteractionsModalContent } from '../../CommentInteractionsModalContent/CommentInteractionsModalContent';
import { useModal } from '../../Modal/useModal';
import CommentActionsContainer from './CommentActionsContainer';
import { TCommentProps as TListCommentProps } from '../CommentsList';
import { CommentOrigin } from './CommentOrigin';
import { CommentAuthor } from './CommentAuthor';
import { renderCommentContent } from '../services/renderCommentContent';
import { useAnalytics } from '../../AnalyticsProvider/AnalyticsProvider';
import { TDropdownState } from '../../Dropdown/types';

export type TCommentProps = TListCommentProps & {
  userAlreadyLikedComment: boolean;
  updateLike?: Function;
  onDeleteButtonClick: () => void;
  handleOpenCommentThread: () => void;
  loading: boolean;
  handleReplyToComment: () => void;
  isCommentAuthor: boolean;
  parentCommentId?: string;
};

const Comment: React.FC<TCommentProps> = ({
  onDeleteButtonClick,
  author,
  createdAt,
  content,
  id,
  isCommentAuthor,
  likesCount,
  updateLike,
  userAlreadyLikedComment,
  handleOpenCommentThread,
  childCommentsCount,
  loading,
  handleReplyToComment,
  origin,
}) => {
  const { sendEvent } = useAnalytics();
  const { isOpen, openModal, ModalComponent } = useModal();
  const modalTitle = `${likesCount} likes`;

  const onCommentDropdownStateChange = ({ isOpen }: TDropdownState) => {
    if (isOpen) {
      sendEvent('comment_options_dropdown_open', { comment_id: id });
    } else {
      sendEvent('comment_options_dropdown_close', { comment_id: id });
    }
  };

  return (
    <>
      <ModalComponent title={modalTitle}>
        <CommentInteractionsModalContent commentId={id} isOpen={isOpen} />
      </ModalComponent>
      <div className="w-full flex flex-col gap-y-4 pt-8 pb-0 px-2">
        <div className="flex justify-between items-center">
          <div className="flex flex-col md:flex-row gap-x-2 items-start md:items-center">
            <CommentAuthor author={author} commentOrigin={origin} />
            <Typo
              variant="small-p"
              text={`${formatDistanceStrict(new Date(), new Date(createdAt))} ago`}
              className="ml-11 -mt-4 md:ml-0 md:mt-0 self-start md:self-auto !text-sm md:!text-base text-gray-600"
            />
            <CommentOrigin origin={origin} />
          </div>
          {isCommentAuthor && (
            <div className="h-[25px]">
              <Dropdown
                onStateChange={onCommentDropdownStateChange}
                label={<ThreeDotsIcon />}
                menu={
                  <CommentDropdownMenu
                    commentId={id}
                    onDeleteButtonClick={onDeleteButtonClick}
                    isCommentAuthor={isCommentAuthor}
                  />
                }
                position="right"
              />
            </div>
          )}
        </div>
        <Typo
          variant="small-p"
          text={renderCommentContent(content as string)}
          className="!text-base !text-black-700 break-words whitespace-pre-line"
        />
        <CommentActionsContainer
          id={id}
          updateLike={updateLike}
          openModal={openModal}
          likesCount={likesCount}
          userAlreadyLikedComment={userAlreadyLikedComment}
          handleOpenCommentThread={handleOpenCommentThread}
          loading={loading}
          childCommentsCount={childCommentsCount as number}
          onReplyButtonClick={handleReplyToComment}
          commentOrigin={origin}
        />
      </div>
    </>
  );
};

export default Comment;
